import { Award, Users, Heart, Shield } from "lucide-react";

const highlights = [
  { icon: Award, label: "Certifié ACACED" },
  { icon: Users, label: "Élevage familial" },
  { icon: Heart, label: "Socialisation naturelle" },
  { icon: Shield, label: "Suivi vétérinaire" },
];

export default function BreedingSection() {
  return (
    <section
      id="elevage"
      data-testid="breeding-section"
      className="relative py-24 lg:py-32 bg-cream overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-16">
          <span className="font-accent text-autumn text-xl">Notre histoire</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            La Taïga Siberia
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-3xl mx-auto">
            Un élevage familial né d'une passion et du désir de partager
            des chats Sibériens d'exception avec des familles aimantes.
          </p>
        </div>

        {/* Highlights bar */}
        <div className="section-animate flex flex-wrap justify-center gap-4 sm:gap-8 mb-16">
          {highlights.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-2 bg-sand/50 px-4 py-2 rounded-full"
            >
              <item.icon className="w-5 h-5 text-forest" />
              <span className="font-body text-sm font-medium text-forest">
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* Main content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left column - Story */}
          <div className="space-y-8">
            <div className="section-animate">
              <h3 className="font-heading text-2xl font-semibold text-forest mb-4 flex items-center gap-3">
                <span className="w-10 h-1 bg-autumn rounded-full" />
                Notre Projet
              </h3>
              <p className="font-body text-forest/80 leading-relaxed">
                La Taïga Sibéria est née d'une contrainte devenue passion. Souffrant
                d'allergies, ma fille et moi ne pouvions accueillir de chat à la maison.
                En découvrant les qualités hypoallergéniques du Sibérien, un projet
                familial est né : accueillir notre première femelle Sia et partager
                cette magnifique race avec d'autres familles.
              </p>
            </div>

            <div className="section-animate">
              <h3 className="font-heading text-2xl font-semibold text-forest mb-4 flex items-center gap-3">
                <span className="w-10 h-1 bg-autumn rounded-full" />
                Notre Formation
              </h3>
              <p className="font-body text-forest/80 leading-relaxed">
                Désireuse de bien faire les choses, j'ai obtenu mon ACACED (Attestation
                de Connaissances pour les Animaux d'Espèces Domestiques) et j'ai été
                accompagnée par une éleveuse expérimentée. Cette rigueur professionnelle
                garantit le bien-être de nos chats et la qualité de notre élevage.
              </p>
            </div>

            <div className="section-animate">
              <h3 className="font-heading text-2xl font-semibold text-forest mb-4 flex items-center gap-3">
                <span className="w-10 h-1 bg-autumn rounded-full" />
                Un Élevage Strictement Traditionnel
              </h3>
              <p className="font-body text-forest/80 leading-relaxed">
                Nous nous concentrons exclusivement sur le Sibérien traditionnel, sans
                mélange de Neva Masquerade. Nos reproducteurs sont soigneusement
                sélectionnés pour leurs lignées pures, avec des pédigrées exempts de
                colourpoint. Cette exigence garantit les meilleures caractéristiques
                hypoallergéniques possibles.
              </p>
            </div>
          </div>

          {/* Right column - Image + Quote */}
          <div className="space-y-8">
            <div className="section-animate relative">
              <img
                src="https://customer-assets.emergentagent.com/job_siberian-haven/artifacts/jwc98sb0_acfc1cbe-1464-45a8-841d-f85745ca4815.jpg"
                alt="Chats Sibériens à l'élevage"
                className="rounded-3xl shadow-hover w-full h-[350px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/40 to-transparent rounded-3xl" />
            </div>

            <div className="section-animate bg-forest text-white rounded-2xl p-8">
              <p className="font-accent text-2xl text-sand mb-4">
                "Un élevage familial, c'est..."
              </p>
              <ul className="font-body space-y-3 text-cream/90">
                <li className="flex items-start gap-3">
                  <span className="text-autumn mt-1">✓</span>
                  Des chats libres de circuler dans toute la maison
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-autumn mt-1">✓</span>
                  Une socialisation naturelle avec toute la famille
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-autumn mt-1">✓</span>
                  Des chatons habitués aux bruits du quotidien
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-autumn mt-1">✓</span>
                  Maximum 1 à 2 portées par an pour préserver la qualité
                </li>
              </ul>
            </div>

            <div className="section-animate bg-sand/50 rounded-2xl p-6">
              <h4 className="font-heading text-lg font-semibold text-forest mb-3">
                Suivi Santé Rigoureux
              </h4>
              <p className="font-body text-sm text-forest/80 mb-4">
                Nos chats sont suivis régulièrement par notre vétérinaire et testés pour :
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/80 rounded-lg px-3 py-2 text-center">
                  <span className="font-body text-xs font-semibold text-forest">HCM</span>
                  <p className="text-xs text-forest/60">Cardiaque</p>
                </div>
                <div className="bg-white/80 rounded-lg px-3 py-2 text-center">
                  <span className="font-body text-xs font-semibold text-forest">PKD</span>
                  <p className="text-xs text-forest/60">Rénale</p>
                </div>
                <div className="bg-white/80 rounded-lg px-3 py-2 text-center">
                  <span className="font-body text-xs font-semibold text-forest">PKdef</span>
                  <p className="text-xs text-forest/60">Génétique</p>
                </div>
                <div className="bg-white/80 rounded-lg px-3 py-2 text-center">
                  <span className="font-body text-xs font-semibold text-forest">Vaccins</span>
                  <p className="text-xs text-forest/60">Annuels</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
